"use client";
import { Box, Container, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";
import { FlexBox } from "@/src/components/FlexBox";
import { Typography as MuiTypography } from "@mui/material";

function Text(props: any) {
  const { type, text, displayName, ...rest } = props;
  return (
    <MuiTypography
      gutterBottom
      className={"w-full gradient-text"}
      style={{
        ...rest,
        justifyContent: rest.textAlign,
        background: rest.background || "#000",
      }}
    >
      {props.children || props.text}
    </MuiTypography>
  );
}

function Title(props: any) {
  const { type, text, displayName, size, className, ...rest } = props;
  return (
    <MuiTypography
      gutterBottom
      variant={size}
      style={{
        ...rest,
        justifyContent: rest.textAlign,
        background: rest.background || "#000",
      }}
      className={cn("w-full gradient-text", className)}
    >
      {text}
    </MuiTypography>
  );
}

import {
  Accordion as UiAccordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/src/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/src/components/ui/collapsible";
import { Card } from "@/src/components/Card";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { cn } from "@/src/shared/utils/mergeTwClasses";
export function Accordion({ items, ...props }: any) {
  return (
    <UiAccordion {...props} type={props.accordionType} collapsible>
      {items.map((item: any, index: number) => (
        <AccordionItem key={item.value} value={item.value}>
          <AccordionTrigger className="text-dark-gray [&_path]:fill-[#009dd3]">
            {item.label}
          </AccordionTrigger>
          <AccordionContent>
            <div className={"pl-11 text-dark-gray"}>{item.content}</div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </UiAccordion>
  );
}

export function Collapses(props: any) {
  const [open, setOpen] = useState(false);

  const { titleOpen, titleClose, ...restProps } = props;

  return (
    <Collapsible {...restProps} open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger>
        <FlexBox
          id="collapsible-trigger"
          className={
            "items-center justify-center gap-2 font-semibold uppercase"
          }
          style={{ padding: "0" }}
        >
          <Text
            text={open ? titleClose : titleOpen}
            background="rgb(0,157,222)"
          ></Text>
          <WebShopIcon
            name={"arrowDown"}
            className={cn(
              "mb-2 h-5 w-5 shrink-0 transition-transform duration-200 [&_path]:fill-[#009dd3]",
              open && "rotate-180",
            )}
          />
        </FlexBox>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <FlexBox
          canvas
          id="collapsible-content"
          direction="column"
          style={{ padding: "0", flexDirection: "column" }}
        >
          {props.children}
        </FlexBox>
      </CollapsibleContent>
    </Collapsible>
  );
}

const BulletList: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        margin: 0,
        paddingLeft: 0,
        "& li": {
          position: "relative",
          paddingLeft: "32px",
          marginBottom: "8px",
        },
        "& li::before": {
          content: "''",
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          width: "15px",
          height: "15px",
          background:
            "url('/images/custom-bullet.png') no-repeat center/contain",
        },
      }}
    >
      {children}
    </Box>
  );
};

interface IntroSectionProps {}

const IntroSection: FC<IntroSectionProps> = () => {
  const items = [
    {
      label: "Чому договір страхування варто купувати онлайн?",
      value: "why-buy-online",
      content: (
        <BulletList>
          <li>
            <strong>Свобода вибору.</strong> Можливість самостійно обрати
            потрібні умови
          </li>
          <li>
            <strong>Доступність.</strong> Можливість придбати договір
            страхування 24/7 з будь – якої точки світу
          </li>
          <li>
            <strong>Економія часу.</strong> Максимально спрощена форма
            заповнення та інтеграція з базами МВС і Дією для швидкості
            заповнення даних
          </li>
          <li>
            <strong>Безпека.</strong> Процес укладання договору відбувається на
            офіційному сайті компанії з дотриманням усіх вимог законодавства
          </li>
          <li>
            <strong>Сервіс.</strong> При виникненні питань професійна команда
              допоможе знайти на них рішення.
          </li>
        </BulletList>
      ),
    },
    {
      label: "Які договори можна укласти онлайн?",
      value: "contracts-online",
      content: (
        <>
          <p className="pb-2">
            У інтернет-магазині можете укласти наступні договори страхування :
          </p>
          <BulletList>
            <li>Автоцивілка обов’язкова</li>
            <li>
              Автоцивілка добровільна («Комплексне страхування відповідальності,
              яка виникає внаслідок використання (експлуатації) наземних
              транспортних засобів і страхування водіїв та пасажирів від
              нещасних випадків на транспорті»)
            </li>
            <li>Медичне страхування при ДТП</li>
            <li>Зелена картка</li>
            <li>Туризм ( «Комплексне страхування подорожуючих за кордон»)</li>
            <li>Каско ( програма « Автозахист»)</li>
          </BulletList>
        </>
      ),
    },
    {
      label: "Як виглядає процес купівлі договору страхування онлайн?",
      value: "purchase-process",
      content: (
        <>
          <BulletList>
            <li>Швидкий розрахунок вартості договору</li>
            <li>Заповнення даних онлайн</li>
            <li>Ознайомлення з пропозицією договору страхування</li>
            <li>
              Підписання електронного договору OTP кодом, що надійде смс
              повідомленням
            </li>
            <li>Оплата договору 24/7 банківською картою</li>
            <li>Отримання договору на електронну адресу</li>
          </BulletList>
          <p className="pt-2">
            Після оплати Ви отримаєте електронний договір з цифровим підписом
            страхової компанії на електронну адресу, вказану у замовленні.
            Електронний договір має таку саму юридичну силу, як і паперовий.
          </p>
        </>
      ),
    },
    {
      label:
        "На що краще звернути увагу при укладанні договору страхування на сайті інтернет-магазину",
      value: "attention",
      content: (
        <BulletList>
          <li>
            Перед укладанням договору радимо ознайомитись з умовами страхування
          </li>
          <li>
            Праворуч від форми заповнення даних розміщений Помічник укладання
            договору. При заповненні даних радимо ознайомитись з підказками, що
            розміщенні у Помічнику
          </li>
          <li>
            Просимо уважно перевіряти внесені дані перед підписанням і оплатою
            договору
          </li>
          <li>
            У разі виникнення питань рекомендуємо звертатися за контактами
            інтернет-магазину або пишіть у форму «Написати нам»
          </li>
        </BulletList>
      ),
    },
  ];
  return (
    <Card
      {...{
        style: {
          "box-shadow": "0px 1px 6px 0px rgba(0, 0, 0, 0.12)",
          gap: "16px",
          borderRadius: "8px",
          background: "#FFFFFF",
          padding: "32px",
        },
      }}
      id={"FaqList"}
    >
      <FlexBox style={{ flexDirection: "column", gap: 24 }}>
        <Title
          text={"Поширені питання:"}
          size={"h2"}
          className={"mb-0 text-dark-blue"}
          background={"#234678"}
        />
        <Accordion items={items} className={"-mt-4"} />
        {/*<Collapses titleOpen={"Показати більше"} titleClose={"Приховати"} />*/}
      </FlexBox>
    </Card>
  );
};

export default IntroSection;
